import { inject, Inject, Injectable } from '@angular/core';
import { RudderAnalytics } from '@rudderstack/analytics-js';
import { EnvironmentService } from '../environment-service/environment.service';
import { WINDOW } from '@constants/global-objects.constants';
import { Observable, Subject } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { LocalStorageService } from '@services/local-storage/local-storage.service';
import { RUDDERSTACK_USER_ID_QUERY_PARAM_NAME } from "@constants/app.constants";

@Injectable({
  providedIn: 'root',
})
export class RudderStackService {
  private analytics: RudderAnalytics;
  private _loadedSubject = new Subject<void>();
  private _readySubject = new Subject<void>();
  private _anonymousId: string;
  private localStorageService: LocalStorageService = inject(LocalStorageService);
  public onLoaded$: Observable<void>;
  public onReady$: Observable<void>;

  readonly N0_RL_ANONYMOUS_ID = 'n0_rl_anonymous_id';
  readonly N0_RL_QUERY_ANON_ID = RUDDERSTACK_USER_ID_QUERY_PARAM_NAME;

  constructor(@Inject(WINDOW) private readonly window: Window) {
    this.analytics = new RudderAnalytics();
    this.analytics.setAnonymousId(this.getAnonymousId());
    this.analytics.load(
      EnvironmentService.environment.rudderstack.writeKey,
      EnvironmentService.environment.rudderstack.dataPlaneUrl,
      {
        onLoaded: () => {
          this._loadedSubject.next();
        },
      },
    );

    window.rudderanalytics = this.analytics;

    this.onReady$ = this._readySubject.asObservable();
    window.rudderanalytics.ready(() => {
      this._readySubject.next();
    });
  }

  getAnalytics(): RudderAnalytics {
    return this.analytics;
  }

  isEnabled(): boolean {
    return !!this.analytics;
  }

  getAnonymousId(): string | undefined {
    const url = this.window.location.href;
    if (url) {
      const queryAnonId = new URL(url).searchParams.get(this.N0_RL_QUERY_ANON_ID);
      if (queryAnonId) {
        this._anonymousId = queryAnonId;
        this.localStorageService.setItem(this.N0_RL_ANONYMOUS_ID, this._anonymousId);
        return this._anonymousId;
      }
    }

    const localAnonId = this.localStorageService.getItem(this.N0_RL_ANONYMOUS_ID);
    if (typeof localAnonId === 'string' && localAnonId.length > 0) {
      this._anonymousId = localAnonId;
      return this._anonymousId;
    }

    this._anonymousId = uuidv4();
    this.localStorageService.setItem(this.N0_RL_ANONYMOUS_ID, this._anonymousId);
    return this._anonymousId;
  }
}
